import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LeftOutlined, RightOutlined, CheckOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import styles from '../../styles/pages/ErgonomicsTour.module.scss';

// ---------------------------------------------------------------------------
// Тур-онбординг по эргономике демо-таблицы. Портирован из
// PLNG-APP-front-v2 (компонент ErgTour) и адаптирован под визуальный стиль
// PLNG: палитра #1e40af / #1e3a8a, рендер через portal, иконки Ant Design.
// ---------------------------------------------------------------------------

type StepAnim = 'resize' | 'edit' | 'filter' | 'collapse';

const STEPS: Array<{ kicker: string; title: string; desc: string; anim: StepAnim }> = [
  {
    kicker: 'Шаг 1 · Ширина столбцов',
    title: 'Меняйте размер столбцов',
    desc: 'Тяните правую границу заголовка, чтобы подстроить ширину под содержимое.',
    anim: 'resize',
  },
  {
    kicker: 'Шаг 2 · Редактирование',
    title: 'Правьте значения прямо в ячейках',
    desc: 'Кликните по редактируемой ячейке и введите новое значение. Отклонение пересчитается моментально, появится плавающая панель сохранения.',
    anim: 'edit',
  },
  {
    kicker: 'Шаг 3 · Фильтры',
    title: 'Фильтруйте по любым срезам',
    desc: 'Нажмите на стрелку в заголовке — откроется фильтр по значениям или условию.',
    anim: 'filter',
  },
  {
    kicker: 'Шаг 4 · Месяцы',
    title: 'Сворачивайте группы месяцев',
    desc: 'Кликните по стрелке у заголовка месяца — группа свернётся в одну колонку. Разверните обратно тем же кликом.',
    anim: 'collapse',
  },
];

export const ErgonomicsTour: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const last = step === STEPS.length - 1;

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && !last) setStep((i) => i + 1);
      else if (e.key === 'ArrowLeft' && step > 0) setStep((i) => i - 1);
      else if (e.key === 'Enter' && last) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, step, last, onClose]);

  if (!open) return null;

  const s = STEPS[step];

  return createPortal(
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.card} role="dialog" aria-label={s.title}>
        <div className={styles.stage}>
          <div className={styles.grid} />
          {s.anim === 'resize' && <AnimResize />}
          {s.anim === 'edit' && <AnimEdit />}
          {s.anim === 'filter' && <AnimFilter />}
          {s.anim === 'collapse' && <AnimCollapse />}
        </div>

        <div className={styles.body}>
          <div className={styles.kicker}>{s.kicker}</div>
          <div className={styles.title}>{s.title}</div>
          <div className={styles.desc}>{s.desc}</div>
        </div>

        <div className={styles.foot}>
          <div className={styles.dots} aria-hidden="true">
            {STEPS.map((_, i) => (
              <span key={i} className={`${styles.dot} ${i === step ? styles.dotActive : ''}`} />
            ))}
          </div>

          <button
            type="button"
            className={`${styles.btn} ${styles.btnGhost}`}
            onClick={() => setStep((i) => Math.max(0, i - 1))}
            disabled={step === 0}
          >
            <LeftOutlined style={{ fontSize: 11 }} />
            Назад
          </button>
          {!last ? (
            <button
              type="button"
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={() => setStep((i) => i + 1)}
            >
              Далее
              <RightOutlined style={{ fontSize: 11 }} />
            </button>
          ) : (
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={onClose}>
              Готово
              <CheckOutlined style={{ fontSize: 11 }} />
            </button>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

ErgonomicsTour.displayName = 'ErgonomicsTour';

// ---------------------------------------------------------------------------
// Кнопка-триггер «Показать подсказки» рядом с таблицей.
// ---------------------------------------------------------------------------

export const ErgonomicsTourButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button type="button" className={styles.helpBtn} onClick={onClick}>
    <QuestionCircleOutlined style={{ fontSize: 14 }} />
    Показать подсказки
  </button>
);

// ===========================================================================
// Сцены анимаций — статичные SVG/markup, всё движение задаётся CSS-keyframes
// в ErgonomicsTour.module.scss.
// ===========================================================================

const AnimResize: React.FC = () => (
  <div className={styles.anim}>
    <div className={styles.resize}>
      <div className={styles.thead}>
        <div>
          ТН
          <span className={styles.handle} />
        </div>
        <div>План</div>
      </div>
      <div className={styles.tbody}>
        <div>АЛЬБУРЕКС</div>
        <div>657 800</div>
        <div>БЕРИНЕРТ</div>
        <div>550 000</div>
      </div>
      <div className={styles.cursor}>
        <svg viewBox="0 0 22 16" width="22" height="16" fill="none" stroke="currentColor" strokeWidth="1.6">
          <path d="M5 8h12M8 4 4 8l4 4M14 4l4 4-4 4" />
        </svg>
      </div>
    </div>
  </div>
);

const AnimEdit: React.FC = () => (
  <div className={styles.anim}>
    <div className={styles.edit}>
      <div className={styles.row}>
        <div>ТН</div>
        <div>Отклонение</div>
        <div>Фев</div>
      </div>
      <div className={styles.row}>
        <div>АЛЬБУРЕКС</div>
        <div className={styles.plan} />
        <div className={styles.target}>
          <span className={styles.v} />
        </div>
      </div>
      <div className={styles.savebar}>
        <span>Несохранённых</span>
        <span className={styles.count}>1</span>
        <span className={styles.btnSave}>Сохранить</span>
      </div>
      <div className={styles.cursor}>
        <svg viewBox="0 0 14 18" width="14" height="18" fill="currentColor">
          <path d="M1 1v14l4-4 3 6 2-1-3-6h5z" stroke="#fff" strokeWidth="1" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  </div>
);

const AnimFilter: React.FC = () => (
  <div className={styles.anim}>
    <div className={styles.filter}>
      <div className={styles.table}>
        <div className={styles.th}>
          <span>ЦПР</span>
          <span className={styles.thBtn}>
            <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="m3 5 3 3 3-3" />
            </svg>
          </span>
        </div>
        <div className={styles.tr}>
          <div>CSL BEHRING</div>
          <div>657 800</div>
        </div>
        <div className={`${styles.tr} ${styles.r2}`}>
          <div>АМДЖЕН</div>
          <div>4 446 500</div>
        </div>
        <div className={styles.tr}>
          <div>БАЙЕР</div>
          <div>820 000</div>
        </div>
      </div>
      <div className={styles.pop}>
        <div className={styles.chk}>
          <span className={styles.box} />
          <span>CSL BEHRING</span>
        </div>
        <div className={`${styles.chk} ${styles.off}`}>
          <span className={styles.box} />
          <span>АМДЖЕН</span>
        </div>
        <div className={styles.chk}>
          <span className={styles.box} />
          <span>БАЙЕР</span>
        </div>
      </div>
    </div>
  </div>
);

const AnimCollapse: React.FC = () => (
  <div className={styles.anim}>
    <div className={styles.collapse}>
      <div className={`${styles.row} ${styles.h}`}>
        <div>ТН</div>
        <div>Янв</div>
        <div>Фев</div>
        <div>
          <span className={styles.flat}>Мар</span>
          <span className={styles.vchip}>Мар</span>
        </div>
        <div>Апр</div>
      </div>
      <div className={styles.row}>
        <div>АЛЬБУРЕКС</div>
        <div>0</div>
        <div>0</div>
        <div>
          <span className={styles.flat}>110 000</span>
        </div>
        <div>493 350</div>
      </div>
      <div className={styles.row}>
        <div>БЕРИНЕРТ</div>
        <div>0</div>
        <div>110 000</div>
        <div>
          <span className={styles.flat}>110 000</span>
        </div>
        <div>110 000</div>
      </div>
    </div>
  </div>
);
